import { ENDPOINTS } from './config';
import {
  publicRatesFinavestEntrySchema,
  publicRatesProviderEntrySchema,
  type PublicRatesFinavestEntry,
  type PublicRatesProviderEntry,
} from '@finavest/shared/client';

/**
 * Public rates feed — the canonical contract is `publicRatesSchema` in
 * @finavest/shared (schemas/rates.ts, owned by W6a): flat `finavest[]` /
 * `providers[]` arrays keyed by product×term, nullable top-level `stand`.
 *
 * The widget validates each entry with the shared zod schemas and regroups the
 * flat arrays into the per-product view-model the Zinsvergleich renders
 * (SARON first, then Fest by ascending term). Defensive on top of the
 * contract: malformed entries are dropped, never rendered, and a missing
 * Finavest entry for a product×term degrades to "Top-Zins auf Anfrage" —
 * never to a broken table or NaN money values.
 */
export interface ProviderRate {
  name: string;
  ratePct: number;
}

export interface RateProduct {
  /** 'saron' | 'fix_<years>' — view-model key derived from product×term. */
  key: string;
  label: string;
  termYears: number | null;
  /** null = no publishable Finavest entry → "Top-Zins auf Anfrage". */
  finavestRatePct: number | null;
  /**
   * Contract `fresh` mirrored through: false = the Finavest entry sits in the
   * 7–10-day twilight zone (UI adds a "Stand älter als 7 Tage" caveat);
   * null = no Finavest entry at all.
   */
  finavestFresh: boolean | null;
  providers: ProviderRate[];
}

export interface PublicRates {
  /** max(provider stand) — from the feed, or derived when the feed sends null. */
  stand: string;
  products: RateProduct[];
}

/** Display sanity bounds on top of the contract (which leaves rates unbounded). */
const isRate = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v) && v >= -2 && v <= 25;

const productKey = (product: 'saron' | 'fest', termYears: number | null): string =>
  product === 'saron' ? 'saron' : `fix_${termYears}`;

const productLabel = (product: 'saron' | 'fest', termYears: number | null): string =>
  product === 'saron' ? 'SARON' : `Fest ${termYears} ${termYears === 1 ? 'Jahr' : 'Jahre'}`;

function sanitizeProvider(raw: unknown): PublicRatesProviderEntry | null {
  const parsed = publicRatesProviderEntrySchema.safeParse(raw);
  if (!parsed.success) return null;
  const entry = parsed.data;
  if (!entry.label.trim() || !isRate(entry.rate)) return null;
  // A fest row without a term cannot be grouped — drop it.
  if (entry.product === 'fest' && entry.termYears === null) return null;
  return { ...entry, label: entry.label.trim().slice(0, 100) };
}

function sanitizeFinavest(raw: unknown): PublicRatesFinavestEntry | null {
  const parsed = publicRatesFinavestEntrySchema.safeParse(raw);
  if (!parsed.success) return null;
  const entry = parsed.data;
  if (!isRate(entry.rateFrom)) return null;
  if (entry.product === 'fest' && entry.termYears === null) return null;
  return entry;
}

/**
 * Strict-but-forgiving validation + regrouping: bad entries are dropped, a
 * feed without any usable provider row collapses to null (→ empty state).
 */
export function sanitizeRates(raw: unknown): PublicRates | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;

  const providers = (Array.isArray(r.providers) ? r.providers : [])
    .map(sanitizeProvider)
    .filter((x): x is PublicRatesProviderEntry => x !== null);
  if (!providers.length) return null;

  const finavest = (Array.isArray(r.finavest) ? r.finavest : [])
    .map(sanitizeFinavest)
    .filter((x): x is PublicRatesFinavestEntry => x !== null);

  // Group provider rows per product×term; only groups with at least one named
  // provider render (a Finavest-only group has nothing to compare against).
  const groups = new Map<string, RateProduct>();
  for (const p of providers) {
    const termYears = p.product === 'saron' ? null : p.termYears;
    const key = productKey(p.product, termYears);
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        label: productLabel(p.product, termYears),
        termYears,
        finavestRatePct: null,
        finavestFresh: null,
        providers: [],
      };
      groups.set(key, group);
    }
    group.providers.push({ name: p.label, ratePct: p.rate });
  }
  for (const f of finavest) {
    const group = groups.get(productKey(f.product, f.product === 'saron' ? null : f.termYears));
    if (group) {
      group.finavestRatePct = f.rateFrom;
      group.finavestFresh = f.fresh;
    }
  }

  // SARON first, then Fest ascending — deterministic regardless of feed order.
  const products = [...groups.values()].sort((a, b) => {
    if (a.termYears === null || b.termYears === null) {
      return (a.termYears === null ? 0 : 1) - (b.termYears === null ? 0 : 1);
    }
    return a.termYears - b.termYears;
  });

  // Contract: top-level stand = max(provider stand), nullable. Trust it when
  // it parses; otherwise derive the max from the rows we actually render.
  const stand =
    typeof r.stand === 'string' && !Number.isNaN(Date.parse(r.stand))
      ? r.stand
      : providers.map((p) => p.stand).reduce((a, b) => (Date.parse(a) >= Date.parse(b) ? a : b));

  return { stand, products };
}

/**
 * Fetch the feed at most once per page load ON SUCCESS (in-memory only —
 * deliberately NO localStorage: stale rates must never resurface). Failures
 * (null = unreachable/empty/invalid) are NOT memoized: the caller shows the
 * empty state and the next call — one per tab re-activation — retries.
 * Concurrent callers still share a single in-flight request.
 */
let inflight: Promise<PublicRates | null> | null = null;

export function fetchRates(): Promise<PublicRates | null> {
  inflight ??= (async () => {
    try {
      const res = await fetch(ENDPOINTS.rates, { headers: { accept: 'application/json' } });
      if (!res.ok) return null;
      return sanitizeRates(await res.json());
    } catch {
      return null;
    }
  })().then((rates) => {
    if (rates === null) inflight = null; // cache success only — failure may retry
    return rates;
  });
  return inflight;
}
